<template>
  <div class="multimedia_box">
    <div class="multimedia" v-for="item in mediaKeys()" :key="item" :class="{ selected: item == actionSet.multimediaValue.value }" @click="select(item)">
      <div class="icon_wrapper">
        <svg viewBox="0 0 24 24" class="media_icon">
          <path :d="getMediaIconPath(item)" fill="currentColor" />
        </svg>
      </div>
      <div class="media_label">{{ $t(mediaVKS[item]) }}</div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { actionSet } from '@/service/driver/action_set'
import { mediaKeys, mediaVKS } from '@/define/vkcode'

const getMediaIconPath = (id: number) => {
  const paths: Record<number, string> = {
    0xaf: 'M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z', // volume_up
    0xae: 'M18.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z', // vol_down
    0xad: 'M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z', // mute
    0xb3: 'M6 19h4V5H6v14zm8-14v14h4V5h-4z', // play_pause (pause icon)
    0xb1: 'M6 6h2v12H6zm3.5 6l8.5 6V6z', // previous_song
    0xb2: 'M6 6h12v12H6z', // stop_playing
    0xb0: 'M6 18l8.5-6L6 6v12zM16 6v12h2V6z', // next_song
    0xaa: 'M15.5 14h-.79l-.28-.27A6.471 6.471 0 0016 9.5 6.5 6.5 0 109.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z', // search
    0xb4: 'M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z', // mail
    0xb7: 'M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 15h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V8h2v2zm-4 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V8h2v2zm-4 8H7v-2h2v2zm0-4H7v-2h2v2zm0-4H7V8h2v2z', // calculator
    0xac: 'M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z', // WWW_home_page
    0xa6: 'M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z', // web_page_return
    0xa8: 'M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z', // web_page_refresh
    0xb5: 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14.5v-9l6 4.5-6 4.5z', // media_player
  }
  return paths[id] || 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z' // default icon
}

const select = (id: number) => {
  actionSet.multimediaValue.value = id
}
</script>

<style lang="less" scoped>
.multimedia_box {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(84px, 1fr));
  gap: var(--spacing-grid-gap);
  width: 100%;
  padding: var(--spacing-inner-padding);

  .multimedia {
    aspect-ratio: 1.1 / 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: var(--bg-card, rgba(255, 255, 255, 0.9));
    border-radius: var(--radius-s);
    padding: 12px 8px;
    /* 应用统一卡片效果 */
    .mgx_card_item_effect();

    .icon_wrapper {
      width: 32px;
      height: 32px;
      background: var(--color-gray-1);
      border-radius: var(--radius-s);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: var(--transition-base);
      color: var(--color-dark);

      .media_icon {
        width: 18px;
        height: 18px;
        opacity: 0.8;
        transition: var(--transition-base);
      }
    }

    .media_label {
      font-size: 11px;
      color: var(--color-text-hint);
      font-weight: 500;
      transition: var(--transition-base);
      text-align: center;
      line-height: 1.2;
      width: 100%;
      white-space: normal;
      word-break: break-all;
    }
  }
}
</style>
